import React, { useEffect, useRef, useState } from 'react';
import { Grid, Loader, Icon } from 'semantic-ui-react';

import CommonTagsInputWrapper from '../../../common/CommonTagsInputWrapper';
import GridColumnInputWrapper from '../../../common/GridColumnInputWrapper';
import { AccordionWrapper, InputWrapper } from 'react-lib/dist/components-es';
import { locationsActions } from '../../../../behavior/locations/index';
import { BATCH_UPDATE_MODE, entityType, VIEW_MODE } from '../../../../constants';
import GisModal from '../../../gis/GisModal';
import { inventoriesActions } from '../../../../behavior/inventories';
import HiddenMap from '../../../gis/HiddenMap';
import { connect } from 'react-redux';
import { LocationDTO } from '@behavior/inventories/dto/LocationDTO';

type Props = {
  values: any;
  inventorySave: any;
  mode: string;
  gisModalOpen: boolean;
  experiment: any;
  idsDeletePreciseLocationBatch: number[];
  locationList: LocationDTO[];
  getLocationList: () => void;
  errors: any;
  getIsPreciseLocationSet: (id: string) => void;
  isPreciseLocationSet: boolean;
  setFieldValue: (name: string, value: any) => void;
  getExperimentByLocation: (id: number) => void;
  getWatershedIdByLocation: (id: number) => void;
  initialData: any;
  entityTypeProp: string;
  renderLocationFieldOnly: boolean;
  renderExperimentFieldOnly: boolean;
  isLoadingLocationList: boolean;
  disabled: boolean;
};

const ILocationContainer = ({
  values,
  inventorySave,
  mode,
  gisModalOpen,
  experiment,
  idsDeletePreciseLocationBatch,
  locationList,
  getLocationList,
  errors,
  getIsPreciseLocationSet,
  isPreciseLocationSet,
  setFieldValue,
  getExperimentByLocation,
  getWatershedIdByLocation,
  initialData,
  entityTypeProp,
  renderLocationFieldOnly,
  renderExperimentFieldOnly,
  isLoadingLocationList,
  disabled,
}: Props) => {
  const [latestPreciseLocationEditType, setLatestPreciseLocationEditType] = useState('');
  const [initialLocation, setInitialLocation] = useState(values.location);
  const [preciseLocationsToDelete, setPreciseLocationsToDelete] = useState<any>([]);
  const prevInventorySave = useRef(inventorySave); //prevents batch update show/hide location/experiment to trigger the deletePreciseLocation()
  useEffect(() => {
    if (mode != VIEW_MODE) loadOnStart();
  }, [mode]);

  useEffect(() => {
    //update preciseLocation icon when gis modal closes
    if (!gisModalOpen && values.id) getIsPreciseLocationSet(values.id);
  }, [gisModalOpen]);

  useEffect(() => {
    //update preciseLocation value for the validations
    setFieldValue('preciseLocation', isPreciseLocationSet);
  }, [isPreciseLocationSet]);

  useEffect(() => {
    //reset precise location delete conditions when loading new inventory
    setLatestPreciseLocationEditType('');
    setInitialLocation(values.location);
  }, [values.id]);

  useEffect(() => {
    if (inventorySave != null && prevInventorySave.current != inventorySave)
      deletePreciseLocation();
  }, [inventorySave]);

  useEffect(() => {
    if (experiment) setFieldValue('experiment', experiment);
  }, [experiment]);

  const deletePreciseLocation = () => {
    //delete precise location when on batch update
    if (mode == BATCH_UPDATE_MODE) setPreciseLocationsToDelete(idsDeletePreciseLocationBatch);
    //delete precise location when inventory edit
    else if (
      initialLocation &&
      initialLocation.id != values.location.id && //location changed
      !['added', 'updated'].includes(latestPreciseLocationEditType) && //precise location was not updated/saved
      isPreciseLocationSet //precise location exists
    )
      setPreciseLocationsToDelete([values.id]);

    setLatestPreciseLocationEditType('');
    setInitialLocation(values.location);
  };

  const itemsDeletedCallback = () => {
    setPreciseLocationsToDelete(null);
    if (values.id) getIsPreciseLocationSet(values.id);
  };

  const loadOnStart = () => {
    if (!locationList || locationList.length == 0) getLocationList();
  };

  const onLocationSelect = (location: LocationDTO) => {
    if (location) {
      getWatershedIdByLocation(location.id);
      getExperimentByLocation(location.id);
    }
  };

  const getLocationField = () => {
    return (
      <>
        <GridColumnInputWrapper
          width={16}
          fieldClassName="checkboxInline"
          inputClassName="left-floated"
          displayLabelAfter={true}
          labelClassName="checkboxLabelOnRight"
          value={values.outOfBuilding || false}
          name={'outOfBuilding'}
          label="inventory.details.page.location.container.is.out.of.building.input"
          type="checkbox"
          setFieldValue={setFieldValue}
          disabled={disabled}
          errors={errors}
          values={values}
          render={(props: any) => <InputWrapper {...props} />}
        />
        <GridColumnInputWrapper
          name="location"
          label="inventory.details.page.location.container.location.input"
          errors={errors}
          render={(props: any) => (
            <>
              <CommonTagsInputWrapper
                {...props}
                suggestionList={locationList}
                matchProperty="locationInfo"
                displayProps="locationInfo"
                value={values.location}
                maxTags={1}
                onlyUnique={true}
                onSelect={onLocationSelect}
                setFieldValue={setFieldValue}
                disabled={disabled}
                values={values}
              />
              <HiddenMap
                idsToDelete={preciseLocationsToDelete}
                itemsDeletedCallback={itemsDeletedCallback}
              />
            </>
          )}
        />
      </>
    );
  };

  const getExperimentField = () => {
    if (!values.experiment) values.experiment = null;

    return (
      <GridColumnInputWrapper
        name="experiment"
        label="inventory.details.page.location.container.experiment.input"
        errors={errors}
        width={16}
        render={(props: any) => (
          <CommonTagsInputWrapper
            {...props}
            suggestionList={initialData.experimentList}
            matchProperty="name"
            displayProps="name"
            value={values.experiment}
            maxTags={1}
            onlyUnique={true}
            setFieldValue={setFieldValue}
            disabled={disabled}
          />
        )}
      />
    );
  };

  const getLocationContainerContent = () => {
    const { FB_PRECISE_LOCATION_THRESHOLD } = initialData.properties;

    return (
      <Grid stackable columns={2} verticalAlign="bottom">
        {getLocationField()}
        {entityType.inventory === entityTypeProp && (
          <GridColumnInputWrapper
            name="preciseLocation"
            label="inventory.details.page.location.container.precise.location.input"
            setFieldValue={setFieldValue}
            disabled={disabled}
            errors={errors}
            values={values}
            specialRequiredCondition={
              (values.location &&
                (values.location.surface > Number(FB_PRECISE_LOCATION_THRESHOLD) ||
                  (values.location.locationInfo && !values.location.locationInfo.includes('-')))) ||
              false
            }
            render={() => (
              <>
                <GisModal
                  selectedInventory={values}
                  mode={mode}
                  onItemEdited={(editType: string) => setLatestPreciseLocationEditType(editType)}
                />
                {isPreciseLocationSet && (
                  <Icon name="check circle outline" size="large" color="green" />
                )}
              </>
            )}
          />
        )}
        {getExperimentField()}
      </Grid>
    );
  };

  return renderLocationFieldOnly && renderExperimentFieldOnly ? (
    <>
      {getLocationField()}
      {getExperimentField()}
    </>
  ) : renderLocationFieldOnly ? (
    getLocationField()
  ) : renderExperimentFieldOnly ? (
    getExperimentField()
  ) : (
    <AccordionWrapper
      closable={false}
      accordionTitle="inventory.details.page.location.container.title"
      render={getLocationContainerContent}
      className=""
      titleAddition={isLoadingLocationList ? <Loader active size="tiny" /> : null}
    />
  );
};

const mapStateToProps = (state: any) => ({
  isLoadingLocationList: state.locations.isLoadingLocationList,
  locationList: state.locations.locationList,
  gisModalOpen: state.gisModal.open,
  isPreciseLocationSet: state.inventories.isPreciseLocationSet,
  inventorySave: state.inventories.inventorySave,
  idsDeletePreciseLocationBatch: state.inventories.idsDeletePreciseLocationBatch,
  experiment: state.locations.experiment,
});

const mapDispatchToProps = (dispatch: any) => ({
  getLocationList: () => dispatch(locationsActions.getLocationAutocompleteList(false)),
  getWatershedIdByLocation: (id: number) => dispatch(locationsActions.getWatershedIdByLocation(id)),
  getExperimentByLocation: (id: number) => dispatch(locationsActions.getExperimentByLocation(id)),
  getIsPreciseLocationSet: (id: string) => dispatch(inventoriesActions.isPreciseLocationSet(id)),
});

ILocationContainer.defaultProps = {
  renderLocationFieldOnly: false,
  renderExperimentFieldOnly: false,
};

export default connect(mapStateToProps, mapDispatchToProps)(ILocationContainer);
